<template>
  <div class="welcome-card clients-card">
    <div class="card-header">
      <div class="card-title-group">
        <i class="fas fa-heartbeat card-title-icon"></i>
        <h3 class="card-title">
          Your {{ $filters.customerLabel("plural", true) }}
        </h3>
      </div>
    </div>

    <div class="card-body">
      <div class="clients-summary">
        <div class="clients-count-circle">
          <svg viewBox="0 0 36 36" class="count-progress">
            <path
              class="progress-track"
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              class="progress-fill"
              :stroke-dasharray="`${progressPct}, 100`"
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <span class="count-number">{{
            welcomeCardPatients.activeCount
          }}</span>
        </div>
        <div class="clients-text">
          <div class="active-label">
            {{ welcomeCardPatients.activeCount }} Active
          </div>
          <div class="capacity-label">
            of {{ welcomeCardPatients.capacity }} total
          </div>
        </div>
      </div>

      <div class="clients-dropdown" ref="dropdownRef">
        <div class="clients-input-wrap" @click="openDropdown">
          <i class="fas fa-search input-icon"></i>
          <input
            ref="searchInput"
            type="text"
            class="clients-ask-input"
            :placeholder="`Ask about a ${$filters.customerLabel(
              'singular',
              false
            )}...`"
            v-model="searchQuery"
            @focus="isExpanded = true"
            @blur="onBlur"
          />
          <i
            class="fas fa-chevron-down input-chevron"
            :class="{ 'input-chevron--open': isExpanded }"
            @mousedown.prevent.stop
            @click.stop="toggleDropdown"
          ></i>
        </div>

        <transition name="list-slide">
          <div v-if="isExpanded" class="patient-list">
            <template v-if="isLoading">
              <div
                v-for="n in 4"
                :key="n"
                class="patient-row patient-row--skeleton"
              >
                <div class="patient-avatar skeleton-circle"></div>
                <div class="patient-info">
                  <div class="skeleton-line skeleton-line--name"></div>
                  <div class="skeleton-line skeleton-line--sub"></div>
                </div>
              </div>
            </template>
            <template v-else-if="filteredPatients.length">
              <div
                v-for="p in filteredPatients"
                :key="p.id"
                class="patient-row"
                @mousedown.prevent="selectPatient(p)"
              >
                <div class="patient-avatar">
                  <img
                    v-if="p.profile_picture && !p._avatarError"
                    :src="p.profile_picture"
                    class="avatar-img"
                    :alt="patientInitials(p)"
                    @error="p._avatarError = true"
                  />
                  <span v-else>{{ patientInitials(p) }}</span>
                </div>
                <div class="patient-info">
                  <div class="patient-name">{{ p.first }} {{ p.last }}</div>
                  <div class="patient-sub">{{ p.status }}</div>
                </div>
                <i class="fas fa-comment patient-chat-icon"></i>
              </div>
            </template>
            <div v-else class="card-empty">
              {{ searchQuery ? "No patients found" : "No active patients" }}
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ClientsCard",
  data() {
    return {
      isExpanded: false,
      searchQuery: "",
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      welcomeCardPatients: (state) => state.patients.welcomeCardPatients,
    }),
    isLoading() {
      return this.welcomeCardPatients.loading;
    },
    patients() {
      return this.welcomeCardPatients.data || [];
    },
    filteredPatients() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.patients;
      return this.patients.filter((p) => {
        const full = `${p.first || ""} ${p.last || ""}`.toLowerCase();
        return full.includes(q);
      });
    },
    progressPct() {
      const { activeCount, capacity } = this.welcomeCardPatients;
      if (!capacity) return 0;
      return Math.min(100, (activeCount / capacity) * 100);
    },
  },
  mounted() {
    if (this.user) {
      this.fetchPatients();
    }
  },
  watch: {
    user(val) {
      if (
        val &&
        !this.welcomeCardPatients.activeCount &&
        !this.welcomeCardPatients.loading
      ) {
        this.fetchPatients();
      }
    },
  },
  methods: {
    fetchPatients() {
      this.$store.dispatch("patients/getWelcomeCardPatients", {
        userId: this.user.id,
        isProvider: this.user.isProvider,
      });
    },
    openDropdown() {
      this.isExpanded = true;
      this.$nextTick(
        () => this.$refs.searchInput && this.$refs.searchInput.focus()
      );
    },
    toggleDropdown() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.$nextTick(
          () => this.$refs.searchInput && this.$refs.searchInput.focus()
        );
      } else {
        this.searchQuery = "";
      }
    },
    onBlur() {
      setTimeout(() => {
        this.isExpanded = false;
        this.searchQuery = "";
      }, 150);
    },
    selectPatient(p) {
      this.isExpanded = false;
      this.searchQuery = "";
      this.$emit("select-patient", p);
    },
    patientInitials(p) {
      if (!p) return "?";
      const f = (p.first || "").trim();
      const l = (p.last || "").trim();
      if (f && l) return (f[0] + l[0]).toUpperCase();
      if (f) return f.slice(0, 2).toUpperCase();
      return "?";
    },
  },
};
</script>

<style lang="scss" scoped>
.welcome-card {
  background: var(--oliver-bg-surface, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  border-radius: 14px;
  padding: 10px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100%;
  box-sizing: border-box;
  overflow: visible;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  backdrop-filter: blur(6px);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  background: none;
  border: none;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title-icon {
  font-size: 14px;
  color: var(--oliver-accent);
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--oliver-text);
  margin: 0;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
}

.clients-summary {
  display: flex;
  align-items: center;
  gap: 10px;
}

.clients-count-circle {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.count-progress {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);

  .progress-track {
    fill: none;
    stroke: rgba(0, 0, 0, 0.08);
    stroke-width: 3;
  }

  .progress-fill {
    fill: none;
    stroke: var(--oliver-accent);
    stroke-width: 3;
    stroke-linecap: round;
    transition: stroke-dasharray 0.4s ease;
  }
}

.count-number {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: var(--oliver-text);
}

.clients-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.active-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--oliver-text);
}

.capacity-label {
  font-size: 12px;
  color: var(--oliver-greeting-subtitle);
}

.clients-dropdown {
  position: relative;
  display: flex;
  flex-direction: column;
}

.patient-list {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--oliver-panel-bg, rgba(255, 255, 255, 0.97));
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.08));
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  max-height: 240px;
  overflow-y: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }
}

.patient-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &--skeleton {
    pointer-events: none;
  }
}

.patient-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--oliver-accent);
  color: var(--oliver-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  overflow: hidden;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.patient-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.patient-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--oliver-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.patient-sub {
  font-size: 11px;
  color: var(--oliver-text);
  opacity: 0.55;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.patient-chat-icon {
  font-size: 11px;
  color: var(--oliver-text);
  opacity: 0.4;
  flex-shrink: 0;
  transition: opacity 0.2s ease;

  .patient-row:hover & {
    opacity: 0.8;
  }
}

.skeleton-circle {
  background: rgba(0, 0, 0, 0.07);
  animation: shimmer 1.4s infinite;
}

.skeleton-line {
  height: 10px;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.07);
  animation: shimmer 1.4s infinite;

  &--name {
    width: 70%;
  }

  &--sub {
    width: 45%;
  }
}

@keyframes shimmer {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

.card-empty {
  font-size: 13px;
  color: var(--oliver-greeting-subtitle);
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
}

.clients-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  padding: 0 12px;
  cursor: text;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.07);
  }

  .input-icon {
    font-size: 11px;
    color: var(--oliver-text);
    opacity: 0.5;
    margin-right: 8px;
    flex-shrink: 0;
  }

  .input-chevron {
    font-size: 11px;
    color: var(--oliver-text);
    opacity: 0.5;
    margin-left: 8px;
    transition: transform 0.25s ease;
    cursor: pointer;
    flex-shrink: 0;

    &--open {
      transform: rotate(180deg);
    }
  }
}

.list-slide-enter-active,
.list-slide-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.list-slide-enter,
.list-slide-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.clients-ask-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 13px;
  color: var(--oliver-text);
  padding: 10px 0;
  outline: none;
  cursor: text;

  &::placeholder {
    color: var(--oliver-text);
    opacity: 0.45;
  }
}
</style>
